import React, { useCallback, useEffect, useState } from 'react';
import { OpFormDrawer } from 'components/customAntd/DLS/OpFormDrawer/OpFormDrawer';
import { DRAWER_WIDTH } from 'constants/ui';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { VisitorType } from 'types/VisitorTypeTypes';
import { getRequest, postRequest } from 'api/apiClient';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { User } from 'types/userTypes';
import { VisitorInvite } from 'types/visitorInvitationTypes';
import VisitorList from '../formComponents/VisitorList';
import { formatFullName, hasPermission, transformNullToEmptyString } from 'utils/utils';
import { notification } from 'antd';
import STATUS from 'constants/status';
import { VISITOR_INVITATION_CREATED_ID } from 'constants/userActivities';
import { DATE_TIME_AM_PM_FORMAT, DATE_TIME_FORMAT } from 'constants/dates';
import { fetchVisits } from 'store/slices/visitsSlice';
import { fetchInvitationConfig, fetchInvitationCustomFields } from 'store/slices/visitorInvitationSlice';
import { searchVisitors, searchVisitorsToday } from 'store/slices/visitorSearchSlice';
import { fetchLocations } from 'store/slices/locationsSlice';
import { Location } from 'types/locationTypes';
import { useTranslation } from 'react-i18next';
import { OpDivider } from 'components/customAntd/DLS/OpDivider/OpDivider';
import i18n from 'i18n/i18n';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const now = dayjs();
const defaultScheduleIn = now.minute(0).add(1, 'hour');
const defaultScheduleOut = defaultScheduleIn.add(1, 'hour');

interface InvitationDrawerProps {
    open: boolean;
    onClose: () => void;
    locationIdRef: number;
}

const InvitationDrawer: React.FC<InvitationDrawerProps> = ({ open, onClose, locationIdRef }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const globalLocationId = useSelector((state: RootState) => state.locations.globalLocation?.id);
    const { invitationConfig, invitationCustomFields } = useSelector((state: RootState) => state.visitorInvitation);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const lastSearchParams = useSelector((state: RootState) => state.visitorSearch.lastSearchParams);
    const { locations } = useSelector((state: RootState) => state.locations);

    const [hosts, setHosts] = useState<User[]>([]);
    const [visitorTypes, setVisitorTypes] = useState<VisitorType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const [form] = OpForm.useForm();

    const hasVisitorlogWrite = hasPermission(tokenScopeList, orgId, 'o', 'visitorlog:w');
    const hasAllVisitorsWrite = hasPermission(tokenScopeList, orgId, 'o', 'allvisitors:w');

    const { t } = useTranslation();

    const [initialValues, setInitialValues] = useState({
        siteId: globalLocationId,
        visitorTypeId: visitorTypes[0]?.id,
        scheduleIn: defaultScheduleIn,
        scheduleOut: defaultScheduleOut,
        visitors: [],
    });

    useEffect(() => {
        if (open) {
            const now = dayjs();
            const defaultScheduleIn = now.minute(0).add(1, 'hour');
            const defaultScheduleOut = defaultScheduleIn.add(1, 'hour');

            setInitialValues({
                siteId: locationIdRef,
                visitorTypeId: visitorTypes[0]?.id,
                scheduleIn: defaultScheduleIn,
                scheduleOut: defaultScheduleOut,
                visitors: [],
            });

            form.resetFields(); // Reset form fields to the updated initial values
        }
    }, [open, globalLocationId, visitorTypes, form, locationIdRef]);

    // Fetch visits and red flags
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                if (invitationConfig === null) {
                    await dispatch(fetchInvitationConfig({ orgId }));
                }
                if (invitationCustomFields.length === 0) {
                    await dispatch(fetchInvitationCustomFields({ orgId }));
                }
                if (orgId && locations.length === 0) {
                    dispatch(fetchLocations({ orgId, status: 1 }));
                }

                const hosts = await getRequest(`/orgs/${orgId}/users`);
                const filteredHosts = hosts.data.filter((user: User) => user.status === 1 && (user.hostExclude === 0 || user.hostExclude === null));
                setHosts(filteredHosts);

                const visitorTypes = await getRequest(`/orgs/${orgId}/visitorTypes`);
                setVisitorTypes(visitorTypes.data);
            } catch (error) {
                console.log("Failed to fetch data.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
        // eslint-disable-next-line
    }, [orgId]);


    const handleSubmit = useCallback(async ({ values }: IOnSubmitArgs) => {
        setIsSubmitting(true);
        try {
            if (values.visitors.length === 0) {
                notification.error({
                    message: t('notification.error'),
                    description: t('invitationDrawer.notification.addVisitorError'),
                    placement: 'bottomRight',
                });
                setIsSubmitting(false);
                return;
            }

            const transformedValues = transformNullToEmptyString(values);

            // Filter out numeric keys directly
            const filteredValues = Object.keys(transformedValues).reduce((acc, key) => {
                if (isNaN(Number(key))) {
                    (acc as Record<string, any>)[key] = transformedValues[key];
                }
                return acc;
            }, {} as Record<string, any>);

            let { scheduleIn, scheduleOut, ...payload } = filteredValues;
            if (scheduleOut && scheduleIn && scheduleOut.isSameOrBefore(scheduleIn)) {
                scheduleOut = scheduleOut.add(1, 'day');
            }

            const inviteResponse = await postRequest(`/orgs/${orgId}/visitorInvitation?language=${i18n.language.split('-')[0]}`, {
                ...payload,
                scheduleIn: scheduleIn?.format(DATE_TIME_FORMAT),
                scheduleOut: scheduleOut?.format(DATE_TIME_FORMAT),
                visitors: transformedValues.visitors.map((visitor: VisitorInvite) => ({
                    // ...visitor,
                    firstName: visitor.firstName?.trim(),
                    middleName: visitor.middleName?.trim(),
                    lastName: visitor.lastName?.trim(),
                    email: visitor.email?.trim(),
                    mobilePhone: visitor.mobilePhone?.trim(),
                    status: STATUS.PENDING.id,
                })),
            });

            const visit = inviteResponse.data?.[0];

            const customFields = Object.keys(transformedValues).reduce((acc, key) => {
                if (!isNaN(Number(key))) {
                    (acc as Record<string, any>)[key] = transformedValues[key];
                }
                return acc;
            }, {} as Record<string, any>);

            for (const [fieldId, fieldValue] of Object.entries(customFields)) {
                if (fieldValue !== undefined && fieldValue !== null) {
                    try {
                        await postRequest(`/orgs/${orgId}/visitor/${visit.id}/visitorData`, {
                            fieldId,
                            value: fieldValue,
                        });
                    } catch (error) {
                        console.error(`Failed to add custom field ${fieldId}:`, error);
                    }
                }
            }

            const fullNameList = values.visitors.map((visitor: VisitorInvite) =>
                formatFullName(visitor.firstName, null, visitor.lastName,)).join(', ');

            await postRequest(`/orgs/${orgId}/userActivity`, {
                userId: globalUserId,
                activityId: VISITOR_INVITATION_CREATED_ID,
                details: fullNameList
            });
            notification.success({
                message: t('notification.success'),
                description: t('invitationDrawer.notification.createSuccess'),
                placement: 'bottomRight',
            });

            await dispatch(fetchVisits({ orgId }));
            await dispatch(searchVisitorsToday({ orgId }));
            if (Object.keys(lastSearchParams).length !== 0) {
                await dispatch(searchVisitors({
                    ...lastSearchParams,
                    orgId,
                    siteId: lastSearchParams.siteId || globalLocationId!
                }));
            }

            form.resetFields();
            onClose();
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('invitationDrawer.notification.submissionError'),
                placement: 'bottomRight',
            });
            console.error("Form submission failed:", error);
        } finally {
            setIsSubmitting(false);
        }
    }, [orgId, globalUserId, form, t, dispatch, onClose, globalLocationId, lastSearchParams]);

    const handleFieldsChange = (changedFields: any) => {
        const changedField = changedFields[0];
        if (changedField) {
            const { name, value } = changedField;
            const [field] = name;
            if (field === 'scheduleIn') {
                const scheduleIn = value;
                if (scheduleIn) {
                    const startMoment = dayjs(scheduleIn);
                    const newScheduleOut = startMoment.add(1, 'hour');
                    form.setFieldsValue({ scheduleOut: newScheduleOut });
                }
            } else if (field === 'scheduleOut') {
                const scheduleOut = value;
                const scheduleIn = form.getFieldValue('scheduleIn');

                if (scheduleIn && scheduleOut) {
                    const startMoment = dayjs(scheduleIn);
                    const endMoment = dayjs(scheduleOut);

                    if (startMoment.isSameOrAfter(endMoment)) {
                        form.setFieldsValue({ scheduleIn: endMoment.subtract(1, 'hour') });
                    }
                }
            }
        }
    };

    const renderFormFields = () => {
        return invitationCustomFields.map((formField, index) => {
            const capitalizedLabel = formField.name;
            if (formField.values) {
                const options = formField.values.map((option) => ({
                    label: option.display || option.name,
                    value: option.value,
                }));

                return (
                    <OpForm.Select
                        label={capitalizedLabel}
                        name={formField.id}
                        options={options}
                        key={formField.id || index.toString()}
                        rules={[{ required: formField.required === 1, message: t('invitationDrawer.customField.required', { 'customFieldName': formField.name }) }]}
                        placeholder={formField.placeholder ? formField.placeholder : undefined}
                        allowClear
                    />
                );
            } else {
                return (
                    <OpForm.TextAreaInput
                        label={capitalizedLabel}
                        name={formField.id}
                        key={formField.id || index.toString()}
                        rows={4}
                        rules={[{ required: formField.required === 1, message: t('invitationDrawer.customField.required', { 'customFieldName': formField.name }) }]}
                        placeholder={formField.placeholder ? formField.placeholder : undefined}
                    />
                );
            }
        });
    };

    const filteredHosts = hasAllVisitorsWrite
        ? hosts
        : hosts.filter(host => host.id === globalUserId);

    return (
        <OpFormDrawer
            title={t('invitationDrawer.title')}
            width={DRAWER_WIDTH}
            open={open}
            isFormLoading={loading || isSubmitting}
            onClose={() => {
                form.resetFields();
                onClose();
            }}
            form={form}
            formComponent={
                <OpForm
                    form={form}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    onFieldsChange={handleFieldsChange}
                    hasError={false}
                    defaultButtons={false}
                >
                    <VisitorList form={form} isInviting={true} />

                    <OpForm.Select
                        label={t('invitationDrawer.visitorType.label')}
                        name="visitorTypeId"
                        rules={[{ required: true, message: t('invitationDrawer.visitorType.required') }]}
                        placeholder={t('invitationDrawer.visitorType.placeholder')}
                        options={visitorTypes.map(type => ({
                            label: t(type.languageCode ?? ''),
                            value: type.id
                        }))}
                    />

                    <OpForm.Select
                        label={t('invitationDrawer.siteId.label')}
                        name="siteId"
                        rules={[{ required: true, message: t('invitationDrawer.siteId.required') }]}
                        placeholder={t('invitationDrawer.siteId.placeholder')}
                        options={locations.map((location: Location) => ({
                            label: location.name,
                            value: location.id
                        }))}
                        disabled={true}
                    />

                    <OpForm.DatePicker
                        format={DATE_TIME_AM_PM_FORMAT}
                        showTime
                        minuteStep={5}
                        name="scheduleIn"
                        label={t('invitationDrawer.scheduleIn.label')}
                        rules={[{ required: true, message: t('invitationDrawer.scheduleIn.required') }]}
                    />

                    <OpForm.DatePicker
                        format={DATE_TIME_AM_PM_FORMAT}
                        showTime
                        minuteStep={5}
                        name="scheduleOut"
                        label={t('invitationDrawer.scheduleOut.label')}
                        rules={[{ required: true, message: t('invitationDrawer.scheduleOut.required') }]}
                    />

                    {invitationConfig?.visitCompany.included === 1 && (
                        <OpForm.Input
                            label={t('invitationDrawer.company.label')}
                            name="company"
                            rules={[{
                                required: invitationConfig.visitCompany.required === 1,
                                message: t('invitationDrawer.company.required')
                            }]}
                        />
                    )}

                    {invitationConfig?.host.included === 1 && (
                        <OpForm.Select
                            label={t('invitationDrawer.host.label')}
                            name="hostId"
                            rules={[{
                                required: invitationConfig.host.required === 1 && hosts.length > 0,
                                message: t('invitationDrawer.host.required')
                            }]}
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                            }
                            placeholder={
                                filteredHosts.length === 0
                                    ? t('invitationDrawer.host.placeholderNotAvailable')
                                    : t('invitationDrawer.host.placeholder')
                            }
                            disabled={filteredHosts.length === 0}
                            options={filteredHosts.map(host => ({
                                label: host.identity?.firstName || host.identity?.lastName
                                    ? formatFullName(host.identity.firstName, host.identity.middleName, host.identity.lastName)
                                    : host.identity?.email,
                                value: host.id
                            }))}
                            allowClear
                        />
                    )}

                    {invitationConfig?.visitPurpose.included === 1 && (
                        <OpForm.TextAreaInput
                            label={t('invitationDrawer.purpose.label')}
                            name="purpose"
                            rows={4}
                            rules={[{
                                required: invitationConfig.visitPurpose.required === 1,
                                message: t('invitationDrawer.purpose.required')
                            }]}
                        />
                    )}

                    {(invitationConfig?.visitNotes?.included === 1 && hasVisitorlogWrite) && (
                        <OpForm.TextAreaInput
                            label={t('invitationDrawer.note.label')}
                            name="note"
                            rows={4}
                            rules={[{
                                required: invitationConfig.visitNotes.required === 1,
                                message: t('invitationDrawer.note.required')
                            }]}
                        />
                    )}
                    <OpDivider />
                    {renderFormFields()}
                </OpForm>
            }
        />
    );
};

export default InvitationDrawer;